//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { GridRowsProp } from '@mui/x-data-grid-premium'
import { EthAddr } from '@shared/SchemaTypes'
import React, { useEffect, useState } from 'react'

import { PoolEventCTO, PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'

import { PoolOffchainDataCTO } from '@shared/PoolOffchainDataCTO'
import { poolActions } from '@react/redux/poolSlice'

import { ReportDataShape } from '@shared/ReportDefinitionCTO'
import dayjs, { Dayjs } from 'dayjs'
import ReportExplorer from './ReportExplorer'
import { fetchAllOffchainData } from './reportingUtils'
import { fetchParticipantsInPools, fetchPoolEventCTOs } from '@react/fetchers/PoolFetchers'
import { logOT } from '@shared/loggingShared'
import { RoleEnum } from '@shared/Enums'

export default function PoolEventTimePeriodPage(): React.ReactElement {
  const [loading, setLoading] = useState<boolean>(false)
  const [fetchDataError, setFetchDataError] = useState<any | undefined>(undefined)
  const [rowsForEvents, setRowsForEvents] = useState<GridRowsProp>([])
  const {
    userConnection,
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { chainName },
  } = useOtChainState()
  const poolMergedOverviewCTOs = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs as unknown as Record<EthAddr, PoolOverviewCTO | PoolFlexOverviewCTO>
  )

  const poolOffchainDataCTOs = useOtSelector((state) => state.poolState.poolOffchainDataCTOs)

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | undefined>(undefined)
  const [startDate, setStartDate] = useState<Date>(new Date())
  const [endDate, setEndDate] = useState<Date>(new Date())
  const dateNow = new Date()
  const [startDateStr, setStartDateStr] = useState<string>(
    `${dateNow.getFullYear()}-${String(dateNow.getMonth() + 1).padStart(2, '0')}-${String(dateNow.getDate()).padStart(2, '0')}`
  )
  const [endDateStr, setEndDateStr] = useState<string>(
    `${dateNow.getFullYear()}-${String(dateNow.getMonth() + 1).padStart(2, '0')}-${String(dateNow.getDate()).padStart(2, '0')}`
  )

  const [lenderAddr, setLenderAddr] = useState<EthAddr | undefined>(undefined)
  const [poolAddr, setPoolAddr] = useState<EthAddr | undefined>(undefined)

  const [lenderAddrs, setLenderAddrs] = useState<EthAddr[]>([])

  const [storedPoolOffchainDataCTOs, setStoredPoolOffchainDataCTOs] = useState<Record<EthAddr, PoolOffchainDataCTO>>({})

  const dispatch = useOtDispatch()

  // useEffect(
  //   function initialReportSelect() {
  //     if (
  //       poolMergedOverviewCTOs &&
  //       poolMergedOverviewCTOs['0xaD82DeEb58c230a2190Ea6dD3c2d3b6688A265A0'] &&
  //       storedPoolOffchainDataCTOs &&
  //       storedPoolOffchainDataCTOs['0xaD82DeEb58c230a2190Ea6dD3c2d3b6688A265A0']
  //     ) {
  //       setPoolAddr('0xaD82DeEb58c230a2190Ea6dD3c2d3b6688A265A0')
  //     }
  //     if (lenderAddrs && lenderAddrs.length > 0) {
  //       setLenderAddr('0xF8A28702CB3D58583FD1882b76220B20597cef8A')
  //     }
  //   },
  //   [storedPoolOffchainDataCTOs, poolMergedOverviewCTOs, lenderAddrs]
  // )

  useEffect(
    function setLenderIfIsLenderEffect() {
      if (userConnection?.roleEnum === RoleEnum.LENDER) {
        const lenderAddr = userConnection?.accountAddr as EthAddr
        setLenderAddr(lenderAddr)
      }
    },
    [userConnection]
  )
  useEffect(function initializeDatesEffect() {
    if (chainName === 'LocalHardhat') {
      const startDate = dayjs.tz('2024-11-01', 'UTC').startOf('day')
      const endDate = dayjs.tz('2024-11-30', 'UTC').startOf('day')
      setDateRangeFromExplorer([startDate, endDate])
    } else {
      const startDate = dayjs().tz('UTC').startOf('day')
      const endDate = dayjs().tz('UTC').startOf('day')
      setDateRangeFromExplorer([startDate, endDate])
    }
  }, [])
  useEffect(
    function fetchOffchainDataEffect() {
      try {
        if (serverApiBaseUrl) {
          setLoading(true)
          fetchAllOffchainData(
            serverApiBaseUrl,
            restAPIKey,
            Object.keys(poolMergedOverviewCTOs),
            poolOffchainDataCTOs
          ).then(
            (result: {
              fetchedPoolOffchainData: PoolOffchainDataCTO[]
              poolOffchainDataForPoolAddrs: Record<EthAddr, PoolOffchainDataCTO>
            }) => {
              result.fetchedPoolOffchainData.forEach((poolOffchainDataCTO) => {
                dispatch(poolActions.updatePoolOffchainDataCTO(poolOffchainDataCTO))
              })

              setStoredPoolOffchainDataCTOs(result.poolOffchainDataForPoolAddrs)
            }
          )
        }
      } catch (error: any) {
        logOT.error('Error fetching pool events', error)
        setFetchDataError(error)
      } finally {
        setLoading(false)
      }
    },
    [serverApiBaseUrl, poolMergedOverviewCTOs]
  )

  useEffect(
    function fetchLenderAddrsEffect() {
      try {
        if (serverApiBaseUrl && Object.keys(storedPoolOffchainDataCTOs).length > 0) {
          setLoading(true)
          const poolAddrs = Object.keys(poolMergedOverviewCTOs)
          fetchParticipantsInPools(serverApiBaseUrl, restAPIKey, poolAddrs).then((addrs) => {
            setLenderAddrs(addrs)
          })
        }
      } catch (error: any) {
        logOT.error('Error fetching lender addresses', error)
        setFetchDataError(error)
      } finally {
        setLoading(false)
      }
    },
    [serverApiBaseUrl, storedPoolOffchainDataCTOs, poolMergedOverviewCTOs, lenderAddr]
  )

  const refreshData = () => {
    async function buildRowsEffect(): Promise<void> {
      try {
        logOT.info('Refresh Data', loading)
        if (!loading) {
          setLoading(true)
          setFetchDataError(undefined)

          logOT.info('Events ', startDate, endDate)
          const poolEvents = []

          if (poolAddr === undefined) {
            const poolAddrs = Object.keys(poolMergedOverviewCTOs)

            for (let j = 0; j < poolAddrs.length; j++) {
              const poolAddr = poolAddrs[j]
              const events: PoolEventCTO[] = await fetchPoolEventCTOs(
                serverApiBaseUrl,
                restAPIKey,
                poolAddr,
                lenderAddr,
                startDateStr,
                endDateStr
              )

              poolEvents.push(...events)
            }
          } else {
            const events: PoolEventCTO[] = await fetchPoolEventCTOs(
              serverApiBaseUrl,
              restAPIKey,
              poolAddr,
              lenderAddr,
              startDateStr,
              endDateStr
            )
            poolEvents.push(...events)
          }

          logOT.info('Events ', poolEvents)

          setRowsForEvents(poolEvents)
        }
      } catch (error: any) {
        logOT.error('Error fetching pool events', error)
        setFetchDataError(error)
      } finally {
        setLoading(false)
      }
    }
    buildRowsEffect()
  }

  function setDateRangeFromExplorer(dateRange: [Dayjs, Dayjs]): void {
    console.log('Set Date Range', dateRange[1].toDate(), dateRange[1].toDate().getUTCDate())
    setRowsForEvents([])
    setDateRange(dateRange)
    setStartDate(dateRange[0].toDate())
    setEndDate(dateRange[1].toDate())

    setStartDateStr(dateRange && dateRange.length > 1 ? dateRange[0].format('YYYY-MM-DD') : '')
    setEndDateStr(dateRange && dateRange.length > 1 ? dateRange[1].format('YYYY-MM-DD') : '')
  }
  return (
    <>
      <ReportExplorer
        loading={loading}
        fetchDataError={fetchDataError}
        reportDataShape={ReportDataShape.POOL_EVENTS}
        rows={rowsForEvents}
        dateRange={dateRange}
        setDateRange={setDateRangeFromExplorer}
        poolAddr={poolAddr}
        setPoolAddr={setPoolAddr}
        poolAddrs={Object.keys(poolMergedOverviewCTOs)}
        lenderAddrs={lenderAddrs}
        lenderAddr={lenderAddr}
        setLenderAddr={setLenderAddr}
        refreshData={refreshData}
      />
    </>
  )
}
